import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ArrowLeft, Shield, Mail } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_locate-loved-ones-3/artifacts/5wufybp4_LOGO.jpeg";
const CONTACT_EMAIL = "contact@misyou.in";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="MISYOU" className="w-8 h-8 rounded-lg object-cover" />
              <span className="font-bold text-slate-900">MISYOU</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-24">
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
          <p className="text-muted-foreground">Effective Date: 26th February 2026 (DPDP-aligned)</p>
        </div>

        <Card className="shadow-lg border-slate-100">
          <CardContent className="p-6 sm:p-10 prose prose-slate max-w-none">
            
            <p className="lead text-lg text-slate-600">
              MISYOU ("we", "our", or "us") values your privacy. This Privacy Policy explains how we collect, use, and protect your information when you use our mobile application and website.
            </p>

            <h2>1. Information We Collect</h2>
            <p>We may collect the following information:</p>
            <ul>
              <li>Personal details (name, phone number, email address)</li>
              <li>Information about missing persons (name, photo, last seen location, physical description)</li>
              <li>Device information (IP address, device type, browser type)</li>
              <li>Location data (only if you explicitly allow it)</li>
              <li>Payment information (processed securely through third-party payment gateways)</li>
            </ul>

            <h2>2. How We Use Information</h2>
            <p>We use the collected data to:</p>
            <ul>
              <li>Help locate missing persons</li>
              <li>Display reports publicly within the app to maximize visibility</li>
              <li>Improve app functionality and user experience</li>
              <li>Communicate with users regarding their reports or account</li>
              <li>Process subscription payments</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2>3. Publicly Shared Information</h2>
            <p>
              Any information submitted regarding missing persons (including photos, name, physical description, and last seen location) <strong>will be visible to other users</strong> of the app and may be shared publicly to aid in locating the missing person.
            </p>

            <h2>4. Photo & Biometric Data</h2>
            <p>Photos uploaded may contain facial features. By uploading photos, you confirm:</p>
            <ul>
              <li>You have the legal right or consent to share these images</li>
              <li>The photos are of the actual missing person</li>
              <li>You understand these images will be publicly visible</li>
              <li>You accept responsibility for any legal issues arising from the photos</li>
            </ul>

            <h2>5. Data Accuracy & Responsibility</h2>
            <p>
              Users are solely responsible for ensuring that the information they provide is accurate, truthful, and lawful. We do not independently verify all submissions. Submitting false information may result in account termination and legal action.
            </p>

            <h2>6. Data Security</h2>
            <p>
              We implement reasonable technical and organizational measures to protect your data against unauthorized access, alteration, or destruction. However, no digital platform can guarantee 100% security.
            </p>

            <h2>7. Data Retention Schedule</h2>
            <p>
              We retain the minimum data necessary for the stated purpose, in line with
              §8(7) of the DPDP Act, 2023. Specific retention windows for each category are:
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border border-slate-200 rounded-lg my-3">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-2 border-b border-slate-200">Data category</th>
                    <th className="text-left p-2 border-b border-slate-200">Retention</th>
                    <th className="text-left p-2 border-b border-slate-200">Deletion trigger</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-100">
                    <td className="p-2">Account profile (name, email, phone)</td>
                    <td className="p-2">For the lifetime of the account</td>
                    <td className="p-2">30 days after the user requests account deletion (soft-delete grace window)</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="p-2">Active missing-person case data &amp; photos</td>
                    <td className="p-2">Up to 30 days from posting (15 free + 15 grace)</td>
                    <td className="p-2">Auto-deletion if not extended via subscription. Closed-safe cases retained 2 years for reference.</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="p-2">FIR copy &amp; guardianship documents</td>
                    <td className="p-2">Until the parent case is deleted, then 90 days for audit</td>
                    <td className="p-2">Same as parent case + 90-day cooling-off, then permanently erased</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="p-2">Sightings submitted</td>
                    <td className="p-2">Linked to parent case</td>
                    <td className="p-2">Deleted when the case is deleted</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="p-2">Payment transactions</td>
                    <td className="p-2">8 years</td>
                    <td className="p-2">As mandated by the Income Tax Act &amp; GST law</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="p-2">Evidence / audit logs (IP, UA, action timestamps)</td>
                    <td className="p-2">24 months, or the duration of an active investigation, whichever is longer</td>
                    <td className="p-2">Auto-purged after the retention window</td>
                  </tr>
                  <tr>
                    <td className="p-2">Consent records (version, timestamp, IP)</td>
                    <td className="p-2">7 years after the latest update</td>
                    <td className="p-2">Required to prove lawful processing under DPDP §6</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-sm text-slate-600">
              You can request a copy of your data or its erasure at any time from
              {' '}<Link to="/settings/privacy" className="underline">Settings → Privacy</Link>.
            </p>

            <h2>8. Data Sharing with Authorities</h2>
            <p>We may share user data with law enforcement agencies if:</p>
            <ul>
              <li>Legally required by court order or government request</li>
              <li>Necessary to assist in locating a missing person</li>
              <li>Required to prevent fraud, abuse, or illegal activity</li>
              <li>Needed to protect the safety of any individual</li>
            </ul>

            <h2>8a. Evidence Logging for Safety &amp; Cybercrime Cooperation</h2>
            <p>
              Because MISYOU is a safety-critical platform that is frequently targeted by scammers
              and extortionists, we maintain <strong>internal, admin-only evidence logs</strong> for
              every safety-relevant action. These logs help us cooperate with police investigations
              and make cybercrime reporting easier for affected families.
            </p>
            <p>For each safety-relevant request we retain:</p>
            <ul>
              <li><strong>IP address</strong> and a derived <strong>user-agent / device fingerprint</strong>.</li>
              <li><strong>Server-side timestamps</strong> of logins (success &amp; failure), case creations, sighting submissions, abuse reports, payments and moderator actions.</li>
              <li><strong>Account identifiers</strong> (user ID, email, optional phone) and consent timestamps.</li>
              <li>The verbatim text of <strong>abuse / scam reports</strong>, including any suspect phone numbers and emails the reporter chose to share.</li>
              <li>Content hashes of uploaded photos &mdash; used to flag recycled or AI-generated submissions.</li>
              <li>QR-scan / share events linked to a case (for poster-distribution analytics).</li>
            </ul>
            <p>
              <strong>What we do NOT retain:</strong> MISYOU does not currently host an in-app
              calling or chat feature, and we do not record phone calls or off-platform messages.
              If a scammer contacts you off-platform, please save those screenshots yourself and
              attach them to an abuse report &mdash; we will then preserve them as part of the case
              evidence chain.
            </p>
            <p>
              <strong>How we use these logs:</strong>
            </p>
            <ul>
              <li>To investigate abuse reports and ban repeat offenders.</li>
              <li>To assist your <strong>local police station</strong> when a family files a complaint about extortion, stalking or impersonation linked to a MISYOU case.</li>
              <li>To assist <a href="https://cybercrime.gov.in/" target="_blank" rel="noreferrer"><strong>cybercrime.gov.in</strong></a> investigations (helpline <strong>1930</strong>) on lawful request.</li>
              <li>To respond to court orders, summons, or legitimate written law-enforcement requests.</li>
            </ul>
            <p>
              <strong>What we will NOT do:</strong> we will not sell, rent or share these evidence
              logs with advertisers, data brokers or any commercial third party. They are encrypted
              at rest and accessible only to a small number of vetted MISYOU administrators.
            </p>
            <p>
              <strong>Retention:</strong> evidence logs are retained for up to <strong>24 months</strong>
              from the date of the event, or for as long as required by an active investigation,
              whichever is longer.
            </p>

            <h2>9. Third-Party Services &amp; Cross-Border Data Transfer</h2>
            <p>
              MISYOU is designed as an India-first service and all primary data (cases,
              photos, FIR copies, guardianship documents, audit logs) is stored on
              infrastructure operated within India. However, a limited set of
              transactional data is processed by the following third parties:
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm border border-slate-200 rounded-lg my-3">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-2 border-b border-slate-200">Service</th>
                    <th className="text-left p-2 border-b border-slate-200">Purpose</th>
                    <th className="text-left p-2 border-b border-slate-200">Jurisdiction</th>
                    <th className="text-left p-2 border-b border-slate-200">Data shared</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-slate-100">
                    <td className="p-2">Stripe, Inc.</td>
                    <td className="p-2">Card payment processing for subscriptions</td>
                    <td className="p-2"><strong>United States</strong></td>
                    <td className="p-2">Name, email, card number (handled directly by Stripe — never touches our servers), transaction amount</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="p-2">Paytm Payments Services Ltd.</td>
                    <td className="p-2">UPI &amp; card payment processing (post-onboarding)</td>
                    <td className="p-2">India</td>
                    <td className="p-2">Name, email, transaction amount</td>
                  </tr>
                  <tr className="border-b border-slate-100">
                    <td className="p-2">Google LLC (Google OAuth)</td>
                    <td className="p-2">"Continue with Google" sign-in</td>
                    <td className="p-2">United States</td>
                    <td className="p-2">Name, email, Google profile picture (only if you choose this sign-in method)</td>
                  </tr>
                  <tr>
                    <td className="p-2">Emergent Integrations (AI inference)</td>
                    <td className="p-2">Optional AI image analysis on missing-person photos</td>
                    <td className="p-2">India / United States (provider-routed)</td>
                    <td className="p-2">The specific photo you ask us to analyse</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              These transfers are made strictly on the lawful basis of <strong>necessity for
              performance of a contract</strong> with you (DPDP Act, 2023 §7(d)) and are
              limited to the categories above. None of these providers are located in a
              country notified by the Central Government as restricted under DPDP §16.
            </p>
            <p className="text-sm text-slate-600">
              You can avoid the US-based card processor by paying via UPI or Paytm wallet
              once that option is enabled, or by emailing
              {' '}<a href="mailto:grievance@misyou.in">grievance@misyou.in</a>
              {' '}to arrange an India-only payment.
            </p>

            <h2>10. Your Rights as a Data Principal (DPDP Act, 2023)</h2>
            <p>
              Under §11–13 of the Digital Personal Data Protection Act, 2023, you have the
              following rights with respect to your personal data:
            </p>
            <ul>
              <li><strong>Right of access.</strong> Download a complete copy of the data we hold about you in JSON format from <Link to="/settings/privacy" className="underline">Settings → Privacy</Link>.</li>
              <li><strong>Right to correction &amp; erasure.</strong> Edit your profile from your dashboard, or request a full account erasure (30-day grace window) from the same page.</li>
              <li><strong>Right to withdraw consent.</strong> Withdraw your consent at any time with one click from Settings → Privacy. Withdrawal is as easy as giving it (DPDP §6(4)). Note that withdrawing consent will prevent you from submitting new cases or sightings.</li>
              <li><strong>Right to nominate.</strong> Nominate another individual (e.g., spouse or sibling) to exercise these rights on your behalf in the event of your death or incapacity.</li>
              <li><strong>Right of grievance redressal.</strong> Contact our Grievance Officer (details below). We will acknowledge within 24 hours and substantively respond within 7 days.</li>
              <li><strong>Right of appeal.</strong> If you are not satisfied with our resolution, you have the right to file a complaint with the <strong>Data Protection Board of India</strong>.</li>
            </ul>
            <p>
              All of the above are exposed as in-app actions on{' '}
              <Link to="/settings/privacy" className="underline font-medium">Settings → Privacy</Link>{' '}
              — no email request is needed for the standard cases.
            </p>

            <h2>11. Consent Withdrawal</h2>
            <p>
              You may withdraw consent for data processing at any time from Settings → Privacy.
              Withdrawing consent does not delete your account or past records — for that,
              use the "Delete my account" action separately. Once consent is withdrawn, you
              will not be able to submit new cases or sightings until you re-accept the
              policy.
            </p>

            <h2>12. Children's Privacy</h2>
            <p>
              We do not knowingly collect personal data from children under 18 without parental or guardian consent. If you believe a child has provided us with personal information, please contact us immediately.
            </p>

            <h2>13. Service Scope</h2>
            <p>
              MISYOU is currently available only to users located in India. All data is collected, stored, and processed within India under Indian data protection laws. International availability may be considered in the future and will be announced separately.
            </p>

            <h2>14. Governing Law &amp; Compliance</h2>
            <p>This Privacy Policy is governed by and complies with:</p>
            <ul>
              <li><strong>Digital Personal Data Protection Act, 2023</strong> (primary law)</li>
              <li>Information Technology Act, 2000</li>
              <li>Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011 — to the extent not superseded by DPDP, 2023</li>
              <li>The Bharatiya Nyaya Sanhita, 2023 &amp; the Protection of Children from Sexual Offences (POCSO) Act, 2012 — for the safety-related obligations of a missing-persons platform</li>
            </ul>
            <p>
              For all DPDP-related queries, please contact our Grievance Officer (details
              in Section 17 below). Any dispute arising out of this Privacy Policy shall be
              subject to the exclusive jurisdiction of the courts of <strong>Talwandi Sabo,
              Punjab, India</strong>.
            </p>

            <h2>15. Changes to Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify users of significant changes through the app or via email. Continued use of the app after changes constitutes acceptance of the updated policy.
            </p>

            <h2>16. Contact Us</h2>
            <div className="bg-slate-50 p-4 rounded-lg mt-4">
              <p className="flex items-center gap-2 mb-2">
                <Mail className="w-5 h-5 text-primary" />
                <strong>Email:</strong> <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
              </p>
              <p className="mb-2"><strong>Phone:</strong> +91 81464 99739</p>
              <p className="mb-2"><strong>Business Name:</strong> MISYOU</p>
              <p><strong>Jurisdiction:</strong> Bathinda, Punjab, India</p>
            </div>

            <h2 id="data-security">16A. Data Security &amp; Encryption at Rest</h2>
            <p>
              We take the protection of sensitive uploads seriously. Specifically:
            </p>
            <ul>
              <li>
                <strong>FIR copies and guardianship documents</strong> are stored in
                a dedicated, access-controlled database collection
                (<code>sensitive_documents</code>) and are <strong>encrypted at rest
                with AES-128 in CBC mode (Fernet)</strong>. The encryption key is held
                only in the production server's protected environment, never in source
                code, and is rotated on incident response.
              </li>
              <li>
                These documents are <strong>never returned by any public API</strong>.
                Access by a moderator or admin is recorded with the user ID, timestamp,
                and access count on the document record itself — so unusual access
                patterns (e.g., one account opening hundreds of documents) can be
                spotted and investigated.
              </li>
              <li>
                AI image analysis (an optional feature) <strong>requires explicit
                per-photo consent</strong> from the data principal before any photo is
                sent to the third-party model provider (Gemini, routed via Emergent
                Integrations). You can post a case without ever using this feature.
              </li>
              <li>
                Passwords are hashed with bcrypt (cost factor 12) and are never
                returned by any endpoint, including the DPDP data-export endpoint.
              </li>
            </ul>

            <h2 id="breach-notification">16B. Breach Notification Commitment (DPDP §8(6))</h2>
            <p>
              In the event of a personal-data breach, MISYOU commits to:
            </p>
            <ol>
              <li>
                <strong>Contain</strong> the breach and rotate any compromised secrets
                within <strong>30 minutes</strong> of detection.
              </li>
              <li>
                <strong>Notify the Data Protection Board of India</strong> within
                <strong>72 hours</strong>, and within <strong>24 hours</strong> if
                sensitive uploads (FIR / guardianship documents), children's records,
                or more than 100 data principals are affected.
              </li>
              <li>
                <strong>Notify every affected data principal</strong> by email within
                the same window, with a clear statement of the categories of personal
                data involved, the actions taken, and the actions they can take.
              </li>
              <li>
                <strong>Publish a public incident notice</strong> on
                {' '}<code>/security-incidents</code> within 24 hours of the
                regulatory notification, including a non-personal summary of what
                happened, the number of principals affected (as a range), and our
                mitigation plan.
              </li>
              <li>
                <strong>Conduct a written post-mortem</strong> within 14 days and
                publish a closing update.
              </li>
            </ol>
            <p>
              Our internal runbook (the operational steps the team follows) is
              reviewed at least every six months via a tabletop exercise. It is held
              at <code>/app/memory/BREACH_NOTIFICATION_RUNBOOK.md</code> and is
              available to regulators on request.
            </p>

            <h2 id="grievance-officer">17. Grievance Officer (DPDP Act, 2023 §8(10))</h2>
            <p>
              In accordance with §8(10) of the Digital Personal Data Protection Act, 2023,
              we have appointed a dedicated Grievance Officer to address any queries,
              complaints, or requests relating to your personal data.
            </p>
            <div className="bg-indigo-50 border border-indigo-200 p-4 rounded-lg mt-4" data-testid="grievance-officer-block">
              <p className="font-bold text-indigo-900 mb-2">Grievance Officer</p>
              <p className="mb-1"><strong>Name:</strong> Mr. Naresh Kumar</p>
              <p className="mb-1">
                <strong>Email:</strong>{' '}
                <a href="mailto:grievance@misyou.in" className="underline">grievance@misyou.in</a>
              </p>
              <p className="mb-1">
                <strong>Phone:</strong>{' '}
                <a href="tel:+919041299739" className="underline">+91 90412 99739</a>
              </p>
              <p className="mb-1"><strong>Working hours:</strong> 10:00 – 18:00 IST, Monday to Saturday</p>
              <p className="mt-2">
                <strong>Response SLA:</strong> Acknowledgement within <strong>24 hours</strong>,
                substantive resolution within <strong>7 working days</strong> of receipt of the
                complaint.
              </p>
              <p className="mt-2 text-sm">
                <strong>Right of appeal:</strong> If you are not satisfied with the resolution,
                you may file a complaint with the{' '}
                <strong>Data Protection Board of India</strong>{' '}
                (the appellate authority constituted under Chapter V of the DPDP Act, 2023).
              </p>
            </div>

          </CardContent>
        </Card>

        {/* Related Links */}
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <Link to="/terms">
            <Button variant="outline">Terms & Conditions</Button>
          </Link>
          <Link to="/anti-harassment">
            <Button variant="outline">Anti-Harassment Policy</Button>
          </Link>
          <Link to="/disclaimer">
            <Button variant="outline">Disclaimer</Button>
          </Link>
        </div>
      </main>
    </div>
  );
}
